import {
  ACCOUNT_ID,
  APP_HOST,
  usePublicPortfolioByAccount,
} from '@utils/apiHooks';

import {
  formatWebsiteLink,
  getFullFacebookHttpLink,
  getFullInstagramHttpLink,
  getFullLinkedInHttpLink,
  getFullTwitterHttpLink,
  getFullXingHttpLink,
} from '@utils/fn';

import {
  FooterRootContainer,
  FooterLinksContainer,
  TermsAndCopyrightsContainer,
  Logo,
  CompanyDescription,
  Column,
  ColumnLogo,
  ColumnTitle,
  SocialLinkColumnTitle,
  LinkItem,
  ContactInfo,
  SocialIcons,
  OptionsContainer,
  FootNoteOption,
  CopyrightText,
  FooterContainer,
  CompanyDescriptionContainer,
  FooterContentContainer,
  NewsletterSection,
  NewsletterContent,
  NewsletterTitle,
  NewsletterSubtitle,
  NewsletterTextLine,
  NewsletterHighlightedText,
  NewsletterRegularText,
  NewsletterForm,
  NewsletterFormContainer,
  NewsletterInput,
  NewsletterButton,
  NewsletterTextLineLite,
} from './styles';

import Icon from '@components/Icon';
import { Row } from '@components/LinearLayout';
import { useMemo } from 'react';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { ROUTES } from '@constants/routes';

const PortfolioOverviewFooter = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const { data: portfolios } = usePublicPortfolioByAccount({
    accountId: ACCOUNT_ID,
  });

  const contact = useMemo(
    () => portfolios?.data?.[0]?.account?.contact,
    [portfolios?.data]
  );

  const socialMediaIcons = [
    contact?.facebook && {
      icon: 'social_facebook',
      onClick: () =>
        window.open(getFullFacebookHttpLink(contact.facebook), '_blank'),
    },
    contact?.twitter && {
      icon: 'social_twitter',
      onClick: () =>
        window.open(getFullTwitterHttpLink(contact.twitter), '_blank'),
    },
    contact?.youtube && {
      icon: 'social_youtube',
      onClick: () => window.open(formatWebsiteLink(contact.youtube), '_blank'),
    },
    contact?.linkedin && {
      icon: 'social_linkedin',
      onClick: () =>
        window.open(getFullLinkedInHttpLink(contact.linkedin), '_blank'),
    },
    contact?.instagram && {
      icon: 'social_instagram',
      onClick: () =>
        window.open(getFullInstagramHttpLink(contact.instagram), '_blank'),
    },
    contact?.xing && {
      icon: 'social_xing',
      onClick: () => {
        window.open(getFullXingHttpLink(contact.xing), '_blank');
      },
    },
  ];

  return (
    <FooterRootContainer>
      <FooterContainer>
        {/* <NewsletterSection>
          <NewsletterContent>
            <NewsletterTextLine>
              <NewsletterHighlightedText>{t('subscribe-to-our-newsletter')}</NewsletterHighlightedText>
              <NewsletterRegularText>{t('subscribe-to-our-newsletter-text')}</NewsletterRegularText>
            </NewsletterTextLine>
            <NewsletterTextLine>
              <NewsletterRegularText>{t('subscribe-to-our-newsletter-remaining-text')}</NewsletterRegularText>
            </NewsletterTextLine>
            <NewsletterTextLine>
              <NewsletterTextLineLite>{t('subscribe-to-our-newsletter-sub-text')}</NewsletterTextLineLite>
            </NewsletterTextLine>
          </NewsletterContent>
          <NewsletterForm>
            <NewsletterFormContainer>
              <NewsletterInput 
                type="email" 
                placeholder={t('enter-your-email')}
              />
              <NewsletterButton>
                {t('subscribe')}
              </NewsletterButton>
            </NewsletterFormContainer>
          </NewsletterForm>
        </NewsletterSection> */}

        <FooterContentContainer>
          <ColumnLogo>
            <Logo
              src={
                'https://media.we-conect.com/images/website/we_conect_logo.png'
              }
              alt={`${portfolios?.data?.[0]?.account?.name as string} Logo`}
            />
            <CompanyDescriptionContainer>
              <CompanyDescription>{t('footer-description')}</CompanyDescription>
            </CompanyDescriptionContainer>
          </ColumnLogo>

          <FooterLinksContainer>
            <Column data-section="what-we-do">
              <ColumnTitle>{t('what-we-do')}</ColumnTitle>
              <LinkItem
                onClick={() => {
                  window.location.href = `${t(ROUTES.OUR_PORTFOLIOS)}#${t(ROUTES.OUR_PORTFOLIOS_EVENTS)}`;
                }}
              >
                {t('product-portfolio-page-button-text')}
              </LinkItem>
              <LinkItem
                onClick={() => {
                  window.location.href = `${t(ROUTES.OUR_PORTFOLIOS)}#${t(ROUTES.OUR_PORTFOLIOS_DIGITAL_HUB)}`;
                }}
              >
                {t('media-and-webcasts')}
              </LinkItem>
              <LinkItem
                onClick={() => {
                  window.location.href = `${t(ROUTES.OUR_PORTFOLIOS)}#${t(ROUTES.OUR_PORTFOLIOS_EVENT_TECH)}`;
                }}
              >
                {t('event-tech')}
              </LinkItem>
            </Column>
            <Column data-section="the-company">
              <ColumnTitle>{t('the-company')}</ColumnTitle>
              <LinkItem
                onClick={() => {
                  window.location.href = t(ROUTES.OUR_TEAM_URL);
                }}
              >
                {t('people')}
              </LinkItem>
              <LinkItem
                onClick={() => {
                  window.location.href = t(ROUTES.ABOUT_US_URL);
                }}
              >
                {t('about-us')}
              </LinkItem>
              <LinkItem
                onClick={() => {
                  window.location.href = t(ROUTES.CAREER_URL);
                }}
              >
                {t('career')}
              </LinkItem>
              <LinkItem
                onClick={() => {
                  window.location.href = t(ROUTES.SOCIAL_RESPONSIBILITY_URL);
                }}
              >
                {t('social-responsibility')}
              </LinkItem>
              <LinkItem
                onClick={() => {
                  window.location.href = t(ROUTES.CONTACT_URL);
                }}
              >
                {t('contact')}
              </LinkItem>
            </Column>

            <Column>
              <SocialLinkColumnTitle>{t('social-links')}</SocialLinkColumnTitle>

              <Row $gap="1rem" margin="1rem 0 0 0">
                <SocialIcons>
                  {socialMediaIcons
                    .filter(item => !!item)
                    .map(item => (
                      <Icon
                        key={item.icon}
                        testId={item.icon}
                        name={item.icon}
                        color={theme.color.black}
                        $hoverColor={theme.color.primary}
                        $clickable
                        onClick={item.onClick}
                        indentLess
                        size={20}
                        $hoverEffect
                      />
                    ))}
                </SocialIcons>
              </Row>

              <ColumnTitle>{t('get-in-touch')}</ColumnTitle>
              <ContactInfo>
                <Icon
                  name={'mail'}
                  color={theme.color.black}
                  $hoverColor={theme.color.primary}
                  $clickable
                  indentLess
                  size={20}
                  $hoverEffect
                />
                {contact?.email}
              </ContactInfo>
              <ContactInfo>
                <Icon
                  name={'phone'}
                  color={theme.color.black}
                  $hoverColor={theme.color.primary}
                  $clickable
                  indentLess
                  size={20}
                  $hoverEffect
                />
                {contact?.phone}
              </ContactInfo>
            </Column>
          </FooterLinksContainer>
        </FooterContentContainer>

        <TermsAndCopyrightsContainer>
          <OptionsContainer>
            <FootNoteOption
              onClick={() => {
                window.location.href = t(ROUTES.IMPRINT_URL);
              }}
            >
              {t('imprint-and-legal-notice')}
            </FootNoteOption>

            <FootNoteOption
              onClick={() => {
                window.location.href = t(ROUTES.TERMS_AND_CONDITIONS_URL);
              }}
            >
              {t('terms-and-conditions')}
            </FootNoteOption>

            <FootNoteOption
              onClick={() => {
                window.location.href = t(ROUTES.PRIVACY_POLICY_URL);
              }}
            >
              {t('privacy@we.CONECT')}
            </FootNoteOption>
          </OptionsContainer>

          <CopyrightText>
            <p>© 2025 we.CONECT Global Leaders Group.</p>
            <p>{t('all-rights-reserved')}</p>
          </CopyrightText>
        </TermsAndCopyrightsContainer>
      </FooterContainer>
    </FooterRootContainer>
  );
};

export default PortfolioOverviewFooter;
